<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.HEADER.1 — the board's slim toolbar. Filters live in the chat
// list header (view switcher, quick-filter chips, status/sort menu — see
// sales-pipeline.HEADER.2); this row only holds what that header does not have:
// pipeline name, search over loaded cards, Group by and the admin-only gear.

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { GROUP_BY } from 'dashboard/helper/kaptionSalesPipeline.js';

defineProps({
  pipelineName: { type: String, default: '' },
  // A header view the board cannot scope to (e.g. Mentions).
  unsupportedView: { type: String, default: null },
  showSettings: { type: Boolean, default: false },
});

const emit = defineEmits(['openSettings', 'refresh']);

const search = defineModel('search', { type: String, default: '' });
const groupBy = defineModel('groupBy', { type: String, default: 'none' });

const { t } = useI18n();

const groupOptions = computed(() => [
  {
    id: GROUP_BY.NONE,
    name: t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.NONE'),
  },
  {
    id: GROUP_BY.ASSIGNEE,
    name: t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.ASSIGNEE'),
  },
  {
    id: GROUP_BY.TEAM,
    name: t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.TEAM'),
  },
]);
</script>

<template>
  <div
    class="flex items-center gap-2 h-9 px-3 border-b border-n-weak bg-n-background"
    data-testid="pipeline-header"
  >
    <i class="i-lucide-kanban size-3.5 text-n-slate-10 shrink-0" />
    <h2
      class="m-0 text-xs font-semibold text-n-slate-12 truncate max-w-[12rem]"
      data-testid="pipeline-name"
    >
      {{ pipelineName }}
    </h2>
    <span
      v-if="unsupportedView"
      class="text-[11px] px-1.5 py-0.5 rounded bg-n-amber-3 text-n-amber-11 truncate"
      data-testid="pipeline-unsupported-view"
    >
      {{ t('KAPTION_PLATFORM.SALES_PIPELINE.UNSUPPORTED_VIEW') }}
    </span>

    <div class="relative ml-2">
      <i
        class="i-lucide-search size-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-n-slate-10"
      />
      <input
        v-model="search"
        type="search"
        class="reset-base h-6 w-44 pl-7 pr-2 text-xs rounded-full border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-teal-8"
        :placeholder="t('KAPTION_PLATFORM.SALES_PIPELINE.SEARCH_PLACEHOLDER')"
        data-testid="pipeline-search"
      />
    </div>

    <div class="flex items-center gap-1.5 ml-auto">
      <div
        class="flex items-center h-6 rounded-full border border-n-weak bg-n-solid-1 p-0.5"
        role="radiogroup"
        :aria-label="t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.LABEL')"
        data-testid="pipeline-group-by"
      >
        <span class="px-1.5 text-[11px] text-n-slate-10">
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.GROUP_BY.LABEL') }}
        </span>
        <button
          v-for="option in groupOptions"
          :key="option.id"
          type="button"
          role="radio"
          :aria-checked="groupBy === option.id"
          class="h-5 !px-2 !py-0 rounded-full text-[11px] transition-colors"
          :class="
            groupBy === option.id
              ? 'bg-n-teal-3 text-n-teal-11 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :data-testid="`pipeline-group-by-${option.id}`"
          @click="groupBy = option.id"
        >
          {{ option.name }}
        </button>
      </div>
      <button
        type="button"
        class="size-6 !p-0 inline-flex items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('KAPTION_PLATFORM.SALES_PIPELINE.REFRESH')"
        @click="emit('refresh')"
      >
        <i class="i-lucide-refresh-cw size-3.5 shrink-0" />
      </button>
      <button
        v-if="showSettings"
        type="button"
        class="size-6 !p-0 inline-flex items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.OPEN')"
        data-testid="pipeline-settings-gear"
        @click="emit('openSettings')"
      >
        <i class="i-lucide-settings size-3.5 shrink-0" />
      </button>
    </div>
  </div>
</template>
